import { audioContext } from "./context.js";

/** Chunk interval. Smaller chunks bound how much is lost if the tab dies. */
const TIMESLICE_MS = 5000;

/** Past this, warn: the whole recording is held in memory until it is saved. */
export const LONG_RECORDING_SECONDS = 45 * 60;

const CANDIDATE_TYPES = [
  "audio/webm;codecs=opus",
  "audio/ogg;codecs=opus",
  "audio/webm",
];

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined;
  return CANDIDATE_TYPES.find((type) => MediaRecorder.isTypeSupported(type));
}

export interface RecorderState {
  supported: boolean;
  recording: boolean;
  seconds: number;
  bytes: number;
  error: string | null;
}

/**
 * Records the master bus to a downloadable file (M6).
 *
 * Tapped after the limiter, so the capture is exactly what was heard - and
 * held independently of the decks, so loading a track never interrupts it.
 * Real time only: it takes as long as the mix does (C6).
 */
export class MixRecorder {
  private readonly destination: MediaStreamAudioDestinationNode | null = null;
  private readonly mimeType = pickMimeType();
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startedAt = 0;
  private bytes = 0;
  private error: string | null = null;

  constructor(source: AudioNode) {
    if (this.mimeType === undefined) {
      this.error = "This browser cannot record audio (MediaRecorder unavailable).";
      return;
    }
    const ctx = audioContext();
    this.destination = ctx.createMediaStreamDestination();
    source.connect(this.destination);
  }

  get supported(): boolean {
    return this.destination !== null;
  }

  state(): RecorderState {
    return {
      supported: this.supported,
      recording: this.recorder?.state === "recording",
      seconds: this.recorder ? (performance.now() - this.startedAt) / 1000 : 0,
      bytes: this.bytes,
      error: this.error,
    };
  }

  start(): boolean {
    if (!this.destination || this.recorder) return false;
    try {
      const recorder = new MediaRecorder(this.destination.stream, { mimeType: this.mimeType });
      this.chunks = [];
      this.bytes = 0;
      this.error = null;
      recorder.ondataavailable = (event) => {
        if (event.data.size === 0) return;
        this.chunks.push(event.data);
        this.bytes += event.data.size;
      };
      recorder.onerror = () => {
        this.error = "Recording stopped unexpectedly.";
      };
      recorder.start(TIMESLICE_MS);
      this.recorder = recorder;
      this.startedAt = performance.now();
      return true;
    } catch (err) {
      this.error = err instanceof Error ? err.message : String(err);
      return false;
    }
  }

  /** Stop and return the finished recording, or null if nothing was captured. */
  async stop(): Promise<Blob | null> {
    const recorder = this.recorder;
    if (!recorder) return null;

    const finished = new Promise<void>((resolve) => {
      recorder.onstop = () => resolve();
    });
    recorder.stop();
    await finished;

    this.recorder = null;
    if (this.chunks.length === 0) return null;

    const blob = new Blob(this.chunks, { type: this.mimeType });
    this.chunks = [];
    return blob;
  }

  extension(): string {
    return this.mimeType?.includes("ogg") ? "ogg" : "webm";
  }
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  // Revoking immediately can cancel the download in some browsers.
  window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
